/**
 * Build a Gift Box — 4 adımlı builder state machine.
 * Spec: research/gift-box-system-plan.md (tek kaynak). ASKIDA — bkz. plan §1.
 *
 * State oturum bazlıdır (sessionStorage) — Favorites'in kalıcı localStorage'ından
 * KASITLI olarak ayrıdır (plan §6): favoriler "sonra bakarım", kutu "şimdi kuruyorum".
 * Aynı anahtarı paylaşsalardı biri diğerini temizlerdi.
 */
(function () {
  const STORAGE_KEY = 'ewa_gift_box_v1';
  const STRINGS = window.aycanGiftBoxStrings || {};

  /* Yer tutucular Liquid'den `[[ad]]` sentinel'i olarak gelir — temanın
     mevcut konvansiyonu (bkz. wholesale form `[[c]]`). `t` filtresine
     değer verilmeyen bir `{{ }}` yer tutucusunun çıktıda sağ kalacağına
     güvenilmiyor; sentinel bu belirsizliği ortadan kaldırıyor. */
  function t(key, vars) {
    let out = STRINGS[key] || '';
    if (vars) {
      Object.keys(vars).forEach((k) => {
        out = out.split(`[[${k}]]`).join(vars[k]);
      });
    }
    return out;
  }

  function formatMoney(cents) {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
    return `$${(cents / 100).toFixed(2)}`;
  }

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  /* Rastgele değil, çakışmayan bir oturum kimliği — sepet satırlarını
     birbirine bağlayan tek şey bu (plan §7). */
  function newBoxId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `gb-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
  }

  class AycanGiftBox extends HTMLElement {
    connectedCallback() {
      this.maxNote = parseInt(this.dataset.maxNote, 10) || 200;
      this.maxPerProduct = parseInt(this.dataset.maxPerProduct, 10) || 2;

      this.steps = [...this.querySelectorAll('[data-gb-step]')];
      this.navItems = [...this.querySelectorAll('[data-gb-stepnav-item]')];
      this.backBtn = this.querySelector('[data-gb-back]');
      this.nextBtn = this.querySelector('[data-gb-next]');
      this.nextLabel = this.querySelector('[data-gb-next-label]');
      this.slotList = this.querySelector('[data-gb-slot-list]');
      this.slotsLine = this.querySelector('[data-gb-slots]');
      this.boxPriceEl = this.querySelector('[data-gb-box-price]');
      this.subtotalEl = this.querySelector('[data-gb-subtotal]');
      this.noteInput = this.querySelector('[data-gb-note]');
      this.noteCount = this.querySelector('[data-gb-note-count]');
      this.itemsTotalEl = this.querySelector('[data-gb-items-total]');
      this.searchInput = this.querySelector('[data-gb-search]');
      this.noResults = this.querySelector('[data-gb-noresults]');
      this.noteCard = this.querySelector('[data-gb-note-card]');
      this.notePreview = this.querySelector('[data-gb-note-preview]');
      this.reviewList = this.querySelector('[data-gb-review-list]');
      this.atcBtn = this.querySelector('[data-gb-atc]');
      this.softMinEl = this.querySelector('[data-gb-softmin]');

      if (!this.steps.length) return;

      this.activeFilter = 'all';

      this.state = this.load();
      this.bind();
      this.restoreBoxSelection();
      this.syncFromRadio();
      this.restoreNote();
      this.applyFilters();
      this.render();
      this.show(this.state.step || 1);
    }

    /* ── State ──────────────────────────────────────────── */
    load() {
      const blank = { v: 1, boxId: newBoxId(), step: 1, items: [], note: '' };
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return blank;
        const parsed = JSON.parse(raw);
        if (!parsed || parsed.v !== 1 || !Array.isArray(parsed.items)) return blank;
        return { ...blank, ...parsed };
      } catch {
        return blank;
      }
    }

    save() {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
      } catch {
        /* private mode / kota — builder oturum içinde çalışmaya devam eder */
      }
    }

    clear() {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        /* yok sayılır */
      }
    }

    /* ── Kutu seçimi ────────────────────────────────────── */
    radios() {
      return [...this.querySelectorAll('.aycan-gb__pack-input')];
    }

    restoreBoxSelection() {
      if (!this.state.variantId) return;
      const match = this.radios().find((r) => r.dataset.gbVariant === String(this.state.variantId));
      if (match && !match.disabled) match.checked = true;
    }

    syncFromRadio() {
      const checked = this.radios().find((r) => r.checked);
      if (!checked) return;
      this.state.size = checked.value;
      this.state.packaging = checked.dataset.gbPackaging;
      this.state.variantId = checked.dataset.gbVariant;
      this.state.boxPrice = parseInt(checked.dataset.gbPrice, 10) || 0;
      this.state.capacity = parseInt(checked.dataset.gbCapacity, 10) || 0;
      this.state.tierTitle = checked.dataset.gbTierTitle;

      /* Kapasite küçüldüyse fazla ürünler taşar — sessizce silmek yerine
         sondan kırp ve özetten görünür olsun. */
      if (this.state.items.length > this.state.capacity) {
        this.state.items = this.state.items.slice(0, this.state.capacity);
      }

      this.querySelectorAll('[data-gb-tier]').forEach((card) => {
        card.classList.toggle('is-selected', card.dataset.gbTier === this.state.size);
      });

      this.save();
    }

    /* Oturum geri yüklendiğinde not ve kart stili DOM'a da yazılmalı —
       yalnız state'te tutmak, geri dönüldüğünde boş bir textarea gösterir. */
    restoreNote() {
      if (this.noteInput && this.state.note) this.noteInput.value = this.state.note;
      const style = this.state.cardStyle || 'minimalist';
      const radio = [...this.querySelectorAll('[data-gb-cardstyle]')].find((r) => r.value === style);
      if (radio) radio.checked = true;
      this.state.cardStyle = style;
      if (this.noteCount) {
        this.noteCount.textContent = t('note_count', {
          count: (this.state.note || '').length,
          max: this.maxNote,
        });
      }
      this.renderNoteCard();
    }

    /* Soft minimum (D4) — tier'ın yarısı yukarı yuvarlanır: 3→2, 5→3, 7→4.
       Plan §4'teki tabloyla birebir örtüşür; ayrı bir ayar açmak iki
       kaynak yaratır ve biri güncellenmeden kalır. */
    softMin() {
      return Math.ceil((this.state.capacity || 0) / 2);
    }

    /* ── Slot işlemleri ─────────────────────────────────── */
    countOf(productId) {
      return this.state.items.filter((i) => String(i.productId) === String(productId)).length;
    }

    canAdd(productId) {
      if (this.state.items.length >= this.state.capacity) return false;
      return this.countOf(productId) < this.maxPerProduct;
    }

    addItem(item) {
      if (!this.canAdd(item.productId)) return false;
      this.state.items.push(item);
      this.save();
      this.render();
      return true;
    }

    removeItem(index) {
      this.state.items.splice(index, 1);
      this.save();
      this.render();
    }

    /* ── Render ─────────────────────────────────────────── */
    render() {
      this.renderSlots();
      this.renderTotals();
      this.renderPool();
      this.renderReview();
      this.renderNav();
    }

    renderSlots() {
      const capacity = this.state.capacity || 0;
      const filled = this.state.items.length;

      if (this.slotsLine) {
        this.slotsLine.textContent = t('slots_filled', { filled, total: capacity });
      }

      if (!this.slotList) return;
      this.slotList.textContent = '';

      this.state.items.forEach((item, index) => {
        const li = el('li', 'aycan-gb__slot aycan-gb__slot--filled');

        /* Küçük görsel — mockup'taki özet satırı. Yoksa nokta'ya düşer,
           böylece görselsiz bir varyantta satır hizası bozulmaz. */
        if (item.image) {
          const thumb = document.createElement('img');
          thumb.className = 'aycan-gb__slot-thumb';
          thumb.src = item.image;
          thumb.alt = '';
          thumb.width = 40;
          thumb.height = 40;
          thumb.loading = 'lazy';
          li.appendChild(thumb);
        } else {
          li.appendChild(el('span', 'aycan-gb__slot-dot'));
        }

        const name = item.variantTitle ? `${item.title} · ${item.variantTitle}` : item.title;
        li.appendChild(el('span', 'aycan-gb__slot-name', name));
        li.appendChild(el('span', 'aycan-gb__slot-price', formatMoney(item.price)));

        const remove = el('button', 'aycan-gb__slot-remove');
        remove.type = 'button';
        remove.setAttribute('aria-label', t('remove_item', { name }));
        remove.innerHTML =
          '<svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" stroke-linecap="round"/></svg>';
        remove.addEventListener('click', () => this.removeItem(index));
        li.appendChild(remove);

        this.slotList.appendChild(li);
      });

      /* Boş slot placeholder'ları (plan §4) — "unfinished box" baskısı
         kurmadan kalan yeri gösterirler. */
      for (let i = filled; i < capacity; i += 1) {
        const li = el('li', 'aycan-gb__slot aycan-gb__slot--empty');
        li.appendChild(el('span', 'aycan-gb__slot-dot'));
        li.appendChild(el('span', 'aycan-gb__slot-name', t('empty_slot')));
        this.slotList.appendChild(li);
      }
    }

    itemsTotal() {
      return this.state.items.reduce((sum, i) => sum + (i.price || 0), 0);
    }

    renderTotals() {
      const boxPrice = this.state.boxPrice || 0;
      if (this.itemsTotalEl) this.itemsTotalEl.textContent = formatMoney(this.itemsTotal());
      if (this.boxPriceEl) {
        this.boxPriceEl.textContent = boxPrice > 0 ? formatMoney(boxPrice) : t('packaging_free');
      }
      if (this.subtotalEl) {
        this.subtotalEl.textContent = formatMoney(boxPrice + this.itemsTotal());
      }
    }

    renderPool() {
      const full = this.state.items.length >= this.state.capacity;
      this.querySelectorAll('[data-gb-product]').forEach((node) => {
        const id = node.dataset.gbProduct;
        const count = this.countOf(id);
        node.classList.toggle('is-in-box', count > 0);
        node.classList.toggle('is-blocked', full || count >= this.maxPerProduct);

        /* Durum rozeti (D12) — seçim değil geri bildirim; aynı ürün birden
           fazla girebildiği için tek/çoğul ayrı anahtar. */
        const flag = node.querySelector('[data-gb-inbox]');
        const text = node.querySelector('[data-gb-inbox-text]');
        if (flag) flag.hidden = count === 0;
        if (text && count > 0) {
          text.textContent = count > 1 ? t('in_box_count', { count }) : t('in_box');
        }
      });
    }

    /* Arama + kategori filtresi birlikte çalışır (VE mantığı): chip kategoriyi
       daraltır, arama onun içinde arar. Ayrı ayrı uygulanırsa kullanıcı
       "filtre neden çalışmadı" diye düşünür. */
    applyFilters() {
      const term = (this.searchInput?.value || '').trim().toLowerCase();
      const active = this.activeFilter || 'all';
      let visible = 0;

      this.querySelectorAll('[data-gb-product]').forEach((node) => {
        const okFamily = active === 'all' || node.dataset.gbFamily === active;
        const okTerm = !term || (node.dataset.gbSearch || '').includes(term);
        const show = okFamily && okTerm;
        node.hidden = !show;
        if (show) visible += 1;
      });

      if (this.noResults) {
        this.noResults.hidden = visible > 0;
        if (!visible) this.noResults.textContent = t('no_results', { term });
      }
    }

    renderNoteCard() {
      if (!this.notePreview) return;
      const text = (this.state.note || '').trim();
      this.notePreview.textContent = text || t('note_preview_empty');
      this.notePreview.classList.toggle('is-placeholder', !text);
      if (this.noteCard) this.noteCard.dataset.style = this.state.cardStyle || 'minimalist';
    }

    renderReview() {
      if (!this.reviewList) return;
      this.reviewList.textContent = '';

      const boxRow = el('li', 'aycan-gb__review-row');
      boxRow.appendChild(el('span', 'aycan-gb__review-name', `${this.state.tierTitle} · ${this.state.packaging}`));
      boxRow.appendChild(
        el('span', 'aycan-gb__review-price', this.state.boxPrice > 0 ? formatMoney(this.state.boxPrice) : t('packaging_free'))
      );
      this.reviewList.appendChild(boxRow);

      this.state.items.forEach((item) => {
        const row = el('li', 'aycan-gb__review-row');
        const name = item.variantTitle ? `${item.title} · ${item.variantTitle}` : item.title;
        row.appendChild(el('span', 'aycan-gb__review-name', name));
        row.appendChild(el('span', 'aycan-gb__review-price', formatMoney(item.price)));
        this.reviewList.appendChild(row);
      });

      if (this.atcBtn) {
        this.atcBtn.disabled = this.state.items.length === 0;
        const label = this.atcBtn.querySelector('[data-gb-atc-label]');
        if (label) {
          label.textContent = t('add_to_cart_total', {
            total: formatMoney((this.state.boxPrice || 0) + this.itemsTotal()),
          });
        }
      }
    }

    renderNav() {
      const step = this.state.step || 1;
      if (this.backBtn) this.backBtn.hidden = step === 1;
      if (this.nextBtn) this.nextBtn.hidden = step === 4;

      if (this.nextLabel) {
        const labels = { 1: 'next_items', 2: 'next_note', 3: 'next_review' };
        /* NOTE adımı atlanabilir (D5) — boş bırakıldığında buton dili
           "devam et" değil "şimdilik atla" olur, kullanıcı zorlanmadığını görsün. */
        if (step === 3 && !(this.noteInput?.value || '').trim()) {
          this.nextLabel.textContent = t('skip_for_now');
        } else {
          this.nextLabel.textContent = t(labels[step] || 'next_review');
        }
      }

      if (this.softMinEl) {
        const short = this.softMin() - this.state.items.length;
        const showOnFill = step === 2 && short > 0 && this.state.items.length > 0;
        this.softMinEl.hidden = !showOnFill;
        if (showOnFill) this.softMinEl.textContent = t('soft_min', { count: short });
      }
    }

    /* ── Adım geçişleri ─────────────────────────────────── */
    show(n) {
      const step = Math.min(Math.max(n, 1), this.steps.length);
      this.state.step = step;
      this.save();

      this.steps.forEach((node) => {
        node.hidden = parseInt(node.dataset.gbStep, 10) !== step;
      });
      this.navItems.forEach((item) => {
        const index = parseInt(item.dataset.gbStepnavItem, 10);
        item.classList.toggle('aycan-gb__stepnav-item--active', index === step);
        item.classList.toggle('aycan-gb__stepnav-item--done', index < step);
      });

      this.renderNav();
      this.renderReview();

      const heading = this.steps[step - 1].querySelector('.aycan-gb__step-h');
      heading?.focus?.();
    }

    /* ── Sepete ekle (plan §7) ──────────────────────────── */
    async addToCart() {
      if (!this.state.variantId || !this.state.items.length) return;

      const boxId = this.state.boxId;
      const note = (this.state.note || '').trim();

      const boxProps = {
        _gift_box_id: boxId,
        _gift_box_capacity: String(this.state.capacity),
      };
      /* Not varsa stili de gitmeli — fulfillment hangi kartı basacağını
         yalnız buradan öğrenir. Not yoksa kart hiç basılmaz, stil de anlamsız. */
      if (note) {
        boxProps._gift_box_note = note;
        boxProps._gift_box_card_style = this.state.cardStyle || 'minimalist';
      }

      const items = [
        { id: parseInt(this.state.variantId, 10), quantity: 1, properties: boxProps },
        ...this.state.items.map((item) => ({
          id: parseInt(item.variantId, 10),
          quantity: 1,
          properties: { ...(item.properties || {}), _gift_box_id: boxId },
        })),
      ];

      const body = { items };
      const drawer = document.querySelector('cart-drawer');
      if (drawer?.getSectionsToRender) {
        body.sections = drawer.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
      }

      this.atcBtn?.classList.add('is-loading');
      if (this.atcBtn) this.atcBtn.disabled = true;

      try {
        const resp = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.description || 'cart error');

        /* D6 — sepete eklendikten sonra oturum state'i temizlenir; sepetteki
           kutu satırı üzerinden rehydrate ayrı bir akış. */
        this.clear();

        if (drawer?.renderContents) {
          drawer.renderContents(data);
          drawer.open?.();
        } else {
          window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
        }
      } catch {
        window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      } finally {
        this.atcBtn?.classList.remove('is-loading');
        if (this.atcBtn) this.atcBtn.disabled = false;
      }
    }

    /* ── Varyant seçici ─────────────────────────────────────
       Grid'ler Liquid'de basılıyor (bkz. aycan-gift-box-swatches snippet) —
       swatch görselleri varyant metafield'ından geliyor ve `/products/x.js`
       metafield taşımıyor. Burada yalnız aç/kapa var. */
    /* Özet satırının küçük görseli. Swatch seçildiyse ONUN görseli daha
       doğru (müşteri o tasarımı seçti); yoksa ürün kartının görseli. */
    thumbFor(item, swatch) {
      return (
        swatch?.querySelector('img')?.src ||
        item.querySelector('.aycan-gb__pool-media img')?.src ||
        ''
      );
    }

    openPicker(item) {
      const picker = item.querySelector('[data-gb-picker]');
      if (!picker) return;
      picker.hidden = !picker.hidden;
    }

    /* Tek seçim: aynı grid içinde bir önceki seçim bırakılır. */
    selectSwatch(swatch) {
      const grid = swatch.closest('[data-gb-picker-grid]');
      if (!grid) return;
      grid.querySelectorAll('.aycan-gb__swatch.is-selected').forEach((other) => {
        if (other !== swatch) {
          other.classList.remove('is-selected');
          other.setAttribute('aria-pressed', 'false');
        }
      });
      const on = !swatch.classList.contains('is-selected');
      swatch.classList.toggle('is-selected', on);
      swatch.setAttribute('aria-pressed', String(on));
    }

    /* Çift seçim tamamlanana kadar "ekle" kapalı — yarım bir çift
       fulfillment'ta ne paketleneceği belirsiz bir satır demek. */
    refreshPairState(item) {
      const addBtn = item.querySelector('[data-gb-pair-add]');
      if (!addBtn) return;
      const picked = [...item.querySelectorAll('[data-gb-picker-grid]')].map((grid) =>
        grid.querySelector('.aycan-gb__swatch.is-selected')
      );
      addBtn.disabled = picked.some((p) => !p);
    }

    addPair(item) {
      const [first, second] = [...item.querySelectorAll('[data-gb-picker-grid]')].map((grid) =>
        grid.querySelector('.aycan-gb__swatch.is-selected')
      );
      if (!first || !second) return;

      const added = this.addItem({
        productId: item.dataset.gbProduct,
        handle: item.dataset.gbHandle,
        title: item.dataset.gbTitle,
        /* Sepete giden yapı plan §7 ile birebir: variant_id = 1. coaster,
           2. coaster bir line item property olarak taşınır. */
        variantId: first.dataset.gbVariant,
        variantTitle: `${first.dataset.gbVariantTitle} + ${second.dataset.gbVariantTitle}`,
        price: parseInt(first.dataset.gbPrice, 10) || 0,
        image: this.thumbFor(item, first),
        properties: { 'Pattern 2': second.dataset.gbVariantTitle },
      });

      if (added) {
        const picker = item.querySelector('[data-gb-picker]');
        item.querySelectorAll('.aycan-gb__swatch.is-selected').forEach((s) => {
          s.classList.remove('is-selected');
          s.setAttribute('aria-pressed', 'false');
        });
        this.refreshPairState(item);
        if (picker) picker.hidden = true;
      }
    }

    bind() {
      this.radios().forEach((radio) => {
        radio.addEventListener('change', () => {
          this.syncFromRadio();
          this.render();
        });
      });

      this.nextBtn?.addEventListener('click', () => this.show((this.state.step || 1) + 1));
      this.backBtn?.addEventListener('click', () => this.show((this.state.step || 1) - 1));

      this.navItems.forEach((navItem) => {
        navItem.addEventListener('click', () => {
          const target = parseInt(navItem.dataset.gbStepnavItem, 10);
          /* İleri atlama yok — tamamlanmamış adımı atlayıp geçmek
             özet ile gerçek durumu ayırır. Geri dönüş serbest. */
          if (target < (this.state.step || 1)) this.show(target);
        });
      });

      this.addEventListener('click', (event) => {
        const addBtn = event.target.closest('[data-gb-add]');
        if (addBtn) {
          const item = addBtn.closest('[data-gb-product]');
          if (!item || item.classList.contains('is-blocked')) return;

          if (item.dataset.gbNeedsPick === 'true') {
            this.openPicker(item);
          } else {
            this.addItem({
              productId: item.dataset.gbProduct,
              handle: item.dataset.gbHandle,
              title: item.dataset.gbTitle,
              variantId: item.dataset.gbVariant,
              variantTitle: '',
              price: parseInt(item.dataset.gbPrice, 10) || 0,
              image: this.thumbFor(item, null),
            });
          }
          return;
        }

        const pairAdd = event.target.closest('[data-gb-pair-add]');
        if (pairAdd) {
          const item = pairAdd.closest('[data-gb-product]');
          if (item) this.addPair(item);
          return;
        }

        const swatch = event.target.closest('.aycan-gb__swatch');
        if (swatch) {
          const item = swatch.closest('[data-gb-product]');
          if (!item) return;

          /* Çift seçimde swatch sepete EKLEMEZ, yalnız işaretler — ekleme
             iki grid de dolduktan sonra tek bir aksiyonla olur. */
          if (item.dataset.gbPair === 'true') {
            this.selectSwatch(swatch);
            this.refreshPairState(item);
            return;
          }

          const added = this.addItem({
            productId: item.dataset.gbProduct,
            handle: item.dataset.gbHandle,
            title: item.dataset.gbTitle,
            variantId: swatch.dataset.gbVariant,
            variantTitle: swatch.dataset.gbVariantTitle,
            price: parseInt(swatch.dataset.gbPrice, 10) || 0,
            image: this.thumbFor(item, swatch),
          });
          if (added) item.querySelector('[data-gb-picker]').hidden = true;
        }
      });

      this.noteInput?.addEventListener('input', () => {
        this.state.note = this.noteInput.value.slice(0, this.maxNote);
        if (this.noteCount) {
          this.noteCount.textContent = t('note_count', {
            count: this.state.note.length,
            max: this.maxNote,
          });
        }
        this.save();
        this.renderNoteCard();
        this.renderNav();
      });

      this.querySelectorAll('[data-gb-cardstyle]').forEach((radio) => {
        radio.addEventListener('change', () => {
          this.state.cardStyle = radio.value;
          this.save();
          this.renderNoteCard();
        });
      });

      /* Arama her tuşta filtreler — 4 ürünlük bir havuzda debounce gereksiz
         karmaşa; katalog büyürse buraya bir gecikme eklenir. */
      this.searchInput?.addEventListener('input', () => this.applyFilters());

      this.querySelectorAll('[data-gb-filter]').forEach((chip) => {
        chip.addEventListener('click', () => {
          this.activeFilter = chip.dataset.gbFilter;
          this.querySelectorAll('[data-gb-filter]').forEach((c) => {
            const on = c === chip;
            c.classList.toggle('is-active', on);
            c.setAttribute('aria-pressed', String(on));
          });
          this.applyFilters();
        });
      });

      /* Kutu kartının tamamı seçilebilir — mockup'ta tik kartın köşesinde,
         yani tıklama hedefi kart. İçerideki paketleme etiketine yapılan
         tıklama kendi radio'sunu zaten seçer, onu ikinci kez ezmeyelim. */
      this.querySelectorAll('[data-gb-tier]').forEach((card) => {
        card.addEventListener('click', (event) => {
          if (event.target.closest('.aycan-gb__pack-label, .aycan-gb__pack-input')) return;
          const radios = [...card.querySelectorAll('.aycan-gb__pack-input')];
          if (radios.some((r) => r.checked)) return;
          const pick = radios.find((r) => !r.disabled);
          if (!pick) return;
          pick.checked = true;
          pick.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      this.querySelector('[data-gb-edit]')?.addEventListener('click', () => this.show(2));

      this.atcBtn?.addEventListener('click', () => this.addToCart());
    }
  }

  if (!customElements.get('aycan-gift-box')) {
    customElements.define('aycan-gift-box', AycanGiftBox);
  }
})();
